import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Slider,
  IconButton,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Code as CodeIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { api } from '../services/api-client';
import { useAuth } from '../contexts/GoogleAuthContext';

// =============================================================================
// SETTING DESCRIPTIONS - Where each setting is used
// =============================================================================

const SETTING_DESCRIPTIONS: Record<string, Record<string, string>> = {
  curation_ai: {
    _module: 'Controls the AI curation pipeline for content processing',
    pipeline: 'Message processing pipeline configuration',
    max_retry_attempts: 'How many times to retry failed curation requests',
    dead_letter_queue_enabled: 'Send failed messages to dead letter queue for manual review',
    dead_letter_config: 'Dead letter queue settings (topic, error handling)',
    batch_processing: 'Batch processing settings for bulk operations',
    location_reviewer: 'Location content review settings',
    metadata: 'Metadata extraction and storage settings',
    character_limits: 'Max characters for different content types',
    monitoring: 'Health checks and alerting configuration',
    pipeline_health_check_interval: 'Seconds between pipeline health checks',
    stale_processing_threshold_seconds: 'Mark processing as stale after this many seconds',
    enable_detailed_tracing: 'Enable verbose Langfuse tracing for debugging',
  },
  controller_service: {
    _module: 'Main controller that routes messages and orchestrates agents',
    timeout: 'Request timeout in seconds',
    max_retries: 'Maximum retry attempts for failed requests',
    enabled: 'Enable/disable the controller service',
  },
  channel_gateway: {
    _module: 'Handles incoming messages from Bird, SMS, Web channels',
    rate_limits: 'Rate limiting per channel/user',
    message_queue: 'Queue settings for async message processing',
  },
  mcp_servers: {
    _module: 'MCP server registry and configuration',
    enabled_servers: 'List of active MCP servers',
    timeouts: 'Per-server timeout settings',
  },
  observability: {
    _module: 'Logging, tracing, and monitoring settings',
    log_request_and_response_to_elasticsearch: 'Send all requests/responses to Elastic',
    langfuse_spans_enabled: 'Enable Langfuse tracing spans',
    alert_on_all_providers_down: 'Alert level when all providers fail',
    alert_on_provider_down: 'Alert level when single provider fails',
  },
  content_controls: {
    _module: 'Content processing and storage rules',
    max_content_chars: 'Maximum characters in processed content',
    raw_response_gcs_bucket: 'GCS bucket for raw API responses',
    store_raw_provider_response: 'When to store raw responses (always/gcs_if_large/never)',
    strip_query_params_for_hash: 'Strip query params when hashing URLs for cache',
    utm_params_to_strip: 'UTM parameters to remove from URLs',
  },
  onboarding_survey: {
    _module: 'User onboarding survey configuration',
    version: 'Survey version for A/B testing',
    questions: 'Survey questions and options',
  },
};

// Get description for a field
const getDescription = (moduleId: string, fieldKey: string): string | undefined => {
  const moduleDescs = SETTING_DESCRIPTIONS[moduleId] || {};
  return moduleDescs[fieldKey] || moduleDescs[fieldKey.toLowerCase()];
};

const getModuleDescription = (moduleId: string): string | undefined => {
  return SETTING_DESCRIPTIONS[moduleId]?._module;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// --- Channel Profile Editor ---
function ChannelEditor({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isDeveloper = user?.role === 'developer' || user?.role === 'admin';

  const { data: profile, isLoading } = useQuery(['channel', channelId], () => api.getChannelProfile(channelId));

  const mutation = useMutation(
    (newProfile: any) => api.updateChannelProfile(channelId, newProfile),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['channels']);
        queryClient.invalidateQueries(['channel', channelId]);
        onClose();
      }
    }
  );

  const [formData, setFormData] = useState<any>(null);

  React.useEffect(() => {
    if (profile) setFormData(JSON.parse(JSON.stringify(profile)));
  }, [profile]);

  if (isLoading || !formData) return <CircularProgress />;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>Editing Channel: {channelId}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={formData.enabled} onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })} disabled={!isDeveloper} />}
            label="Enabled"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography gutterBottom>Max Length (Characters)</Typography>
          <Slider value={formData.max_length || 0} min={0} max={10000} step={10} valueLabelDisplay="auto" onChange={(_, val) => setFormData({ ...formData, max_length: val })} disabled={!isDeveloper} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Formatting Features</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {Object.keys(formData.formatting || {}).map((key) => (
              <FormControlLabel key={key} control={<Switch checked={formData.formatting[key]} onChange={(e) => setFormData({ ...formData, formatting: { ...formData.formatting, [key]: e.target.checked } })} disabled={!isDeveloper} />} label={key.replace('supports_', '').replace(/_/g, ' ')} />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Prompt Rules (XML)" multiline rows={10} value={formData.prompt_rules || ''} onChange={(e) => setFormData({ ...formData, prompt_rules: e.target.value })} disabled={!isDeveloper} sx={{ fontFamily: 'monospace' }} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => mutation.mutate(formData)} disabled={!isDeveloper || mutation.isLoading}>
          {mutation.isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}

// =============================================================================
// MODULE EDITOR - Clean flat layout with proper object handling
// =============================================================================

// Format label from snake_case
const formatLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

// Recursive field renderer
interface FieldProps {
  fieldKey: string;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  moduleId: string;
  path?: string;
  depth?: number;
}

function SettingField({ fieldKey, value, onChange, disabled, moduleId, path = '', depth = 0 }: FieldProps) {
  const fullPath = path ? `${path}.${fieldKey}` : fieldKey;
  const description = getDescription(moduleId, fieldKey);
  const label = formatLabel(fieldKey);

  // Boolean - inline toggle
  if (typeof value === 'boolean') {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
        pl: depth * 2,
        borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none',
      }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
          {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
        </Box>
        <Switch checked={value} onChange={(e) => onChange(e.target.checked)} disabled={disabled} color="success" />
      </Box>
    );
  }

  // Number
  if (typeof value === 'number') {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1,
        pl: depth * 2,
        borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none',
      }}>
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
          {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
        </Box>
        <TextField
          size="small"
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          disabled={disabled}
          sx={{ width: 120 }}
        />
      </Box>
    );
  }

  // Array
  if (Array.isArray(value)) {
    return (
      <Box sx={{ py: 1, pl: depth * 2, borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none' }}>
        <Typography variant="body2" fontWeight={500} gutterBottom>{label}</Typography>
        {description && <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>{description}</Typography>}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {value.map((item, idx) => (
            <Chip
              key={idx}
              label={typeof item === 'object' ? JSON.stringify(item) : String(item)}
              size="small"
              onDelete={disabled ? undefined : () => onChange(value.filter((_, i) => i !== idx))}
              sx={{ bgcolor: '#e3f2fd', maxWidth: 200 }}
            />
          ))}
          {disabled ? null : (
            <Chip
              icon={<AddIcon sx={{ fontSize: 14 }} />}
              label="Add"
              size="small"
              variant="outlined"
              onClick={() => {
                const newVal = prompt('Add item:');
                if (newVal) onChange([...value, newVal]);
              }}
              sx={{ cursor: 'pointer' }}
            />
          )}
          {value.length === 0 && !disabled && (
            <Typography variant="caption" color="text.secondary">Empty array</Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Object - render nested fields
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value);
    return (
      <Box sx={{ py: 1, pl: depth * 2, borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" fontWeight={600} color={depth === 0 ? 'primary' : 'text.primary'}>
            {label}
          </Typography>
          <Chip label={`${entries.length}`} size="small" sx={{ height: 18, fontSize: 11 }} />
          {description && (
            <Tooltip title={description}>
              <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{
          bgcolor: depth === 0 ? '#f8f9fa' : '#fff',
          borderRadius: 1,
          border: '1px solid #e8e8e8',
          p: 1.5,
          ml: depth === 0 ? 0 : 1,
        }}>
          {entries.map(([k, v]) => (
            <SettingField
              key={k}
              fieldKey={k}
              value={v}
              onChange={(newVal) => onChange({ ...value, [k]: newVal })}
              disabled={disabled}
              moduleId={moduleId}
              path={fullPath}
              depth={depth + 1}
            />
          ))}
          {entries.length === 0 && (
            <Typography variant="caption" color="text.secondary">No nested settings</Typography>
          )}
        </Box>
      </Box>
    );
  }

  // String (default)
  const strValue = value ?? '';
  const isLong = typeof strValue === 'string' && (strValue.length > 50 || strValue.includes('\n'));

  return (
    <Box sx={{
      display: 'flex',
      alignItems: isLong ? 'flex-start' : 'center',
      gap: 2,
      py: 1,
      pl: depth * 2,
      borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none',
    }}>
      <Box sx={{ minWidth: 180, pt: isLong ? 1 : 0 }}>
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
        {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
      </Box>
      <TextField
        size="small"
        value={strValue}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        multiline={isLong}
        rows={isLong ? 2 : 1}
        fullWidth
        sx={{ maxWidth: isLong ? '100%' : 300 }}
      />
    </Box>
  );
}

function ModuleEditor({ moduleId, onClose }: { moduleId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isDeveloper = user?.role === 'developer' || user?.role === 'admin';

  const { data: config, isLoading, refetch } = useQuery(['module', moduleId], () => api.getModuleConfig(moduleId));

  const mutation = useMutation(
    (newConfig: any) => api.updateModuleConfig(moduleId, newConfig),
    { onSuccess: () => { queryClient.invalidateQueries(['module', moduleId]); onClose(); } }
  );

  const [formData, setFormData] = useState<any>(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [jsonString, setJsonString] = useState('');
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (config) {
      setFormData(JSON.parse(JSON.stringify(config)));
      setJsonString(JSON.stringify(config, null, 2));
    }
  }, [config]);

  const handleChange = useCallback((key: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = () => {
    try {
      const dataToSave = showRawJson ? JSON.parse(jsonString) : formData;
      setError(null);
      mutation.mutate(dataToSave);
    } catch (e: any) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  React.useEffect(() => {
    if (showRawJson && formData) setJsonString(JSON.stringify(formData, null, 2));
  }, [showRawJson, formData]);

  if (isLoading || !formData) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const fields = Object.entries(formData).filter(([key]) => !['id', 'updated_at', 'updated_by'].includes(key));
  const moduleDesc = getModuleDescription(moduleId);

  return (
    <>
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ color: '#5c6bc0' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{moduleId}</Typography>
            {moduleDesc && <Typography variant="caption" color="text.secondary">{moduleDesc}</Typography>}
          </Box>
          <Tooltip title="Refresh"><IconButton size="small" onClick={() => refetch()}><RefreshIcon /></IconButton></Tooltip>
          <Tooltip title={showRawJson ? 'Form View' : 'JSON View'}>
            <IconButton size="small" onClick={() => setShowRawJson(!showRawJson)} color={showRawJson ? 'primary' : 'default'}>
              <CodeIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {showRawJson ? (
          <TextField
            fullWidth
            multiline
            value={jsonString}
            onChange={(e) => setJsonString(e.target.value)}
            disabled={!isDeveloper}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.5 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& fieldset': { border: 'none' } }}
            minRows={25}
          />
        ) : (
          <Box sx={{ maxHeight: 500, overflow: 'auto', px: 2, py: 1 }}>
            {fields.map(([key, value]) => (
              <SettingField
                key={key}
                fieldKey={key}
                value={value}
                onChange={(val) => handleChange(key, val)}
                disabled={!isDeveloper}
                moduleId={moduleId}
              />
            ))}
            {fields.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No configuration fields</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {fields.length} settings
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDeveloper || mutation.isLoading}>
          {mutation.isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </>
  );
}

// =============================================================================
// MAIN SETTINGS PAGE
// =============================================================================

export default function Settings() {
  const [tab, setTab] = useState(0);
  const { data: channels, isLoading: channelsLoading } = useQuery('channels', api.getChannelProfiles);
  const { data: modules, isLoading: modulesLoading } = useQuery('modules', api.getSettingsModules);

  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  if (channelsLoading || modulesLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Settings & Configuration</Typography>

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Channel Profiles" />
          <Tab label="System Configuration" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Grid container spacing={3}>
            {channels?.map((channel) => (
              <Grid item xs={12} md={4} key={channel.id}>
                <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, height: '100%' }} onClick={() => setSelectedChannel(channel.id)}>
                  <CardContent>
                    <Typography variant="h6">{channel.id.toUpperCase()}</Typography>
                    <Typography color="text.secondary" gutterBottom>Type: {channel.channel_type}</Typography>
                    <Box sx={{ mt: 2 }}>
                      {channel.enabled ? (
                        <Typography color="success.main" variant="body2">● Enabled</Typography>
                      ) : (
                        <Typography color="error.main" variant="body2">● Disabled</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            System configuration modules. Each module controls a different part of the platform.
          </Typography>
          <Grid container spacing={2}>
            {modules?.map((moduleId) => {
              const desc = getModuleDescription(moduleId);
              return (
                <Grid item xs={12} sm={6} md={4} key={moduleId}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f5f5f5', borderColor: '#bbb' },
                      transition: 'all 0.15s',
                      border: '1px solid #e0e0e0',
                      height: '100%',
                    }}
                    onClick={() => setSelectedModule(moduleId)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <SettingsIcon sx={{ color: '#5c6bc0', fontSize: 20, mt: 0.25 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{moduleId}</Typography>
                        {desc && <Typography variant="caption" color="text.secondary">{desc}</Typography>}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>
      </Card>

      {/* Channel Editor Dialog */}
      <Dialog open={!!selectedChannel} onClose={() => setSelectedChannel(null)} maxWidth="md" fullWidth>
        <DialogContent>
          {selectedChannel && <ChannelEditor channelId={selectedChannel} onClose={() => setSelectedChannel(null)} />}
        </DialogContent>
      </Dialog>

      {/* Module Editor Dialog */}
      <Dialog open={!!selectedModule} onClose={() => setSelectedModule(null)} maxWidth="md" fullWidth>
        {selectedModule && <ModuleEditor moduleId={selectedModule} onClose={() => setSelectedModule(null)} />}
      </Dialog>
    </Box>
  );
}
